import { useCallback, useRef } from 'react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/stores/authStore'
import { apiService } from '@/services/apiService'

/**
 * Drives the one-time navigation tour. `shouldRun` is true when the
 * signed-in member has never finished the tour (server-side flag is null)
 * OR when `replay()` has been called this session.
 *
 * `markComplete()` is called by <TutorialTour /> on Joyride STATUS.FINISHED
 * or STATUS.SKIPPED — both are treated as "I've seen it" and persisted via
 * PUT /api/users/profile. Failures don't block the local UI (the tour
 * closes either way); the persisted state catches up on next page load.
 */
export interface TutorialAPI {
  shouldRun: boolean
  markComplete: () => Promise<void>
  replay: () => void
}

export const useTutorial = (): TutorialAPI => {
  const user = useAuthStore((s) => s.user)
  const updateUser = useAuthStore((s) => s.updateUser)
  const replayActive = useAuthStore((s) => s.tutorialReplay)
  const setReplayActive = useAuthStore((s) => s.setTutorialReplay)
  // Tracked via a ref so back-to-back `markComplete()` calls inside the
  // same React tick (e.g. Joyride firing FINISHED then SKIPPED in quick
  // succession) only result in one network write — without waiting for a
  // re-render to flush the `persisted` state.
  const persistedRef = useRef(false)

  const hasSeen = Boolean(user?.tutorialCompletedAt) && !replayActive
  const shouldRun = Boolean(user) && !hasSeen

  const markComplete = useCallback(async () => {
    setReplayActive(false)
    if (persistedRef.current) return
    if (user?.tutorialCompletedAt && !replayActive) {
      persistedRef.current = true
      return
    }

    const iso = new Date().toISOString()
    persistedRef.current = true
    updateUser({ tutorialCompletedAt: iso })
    try {
      await apiService.updateProfile({ tutorialCompletedAt: iso })
    } catch {
      toast.error(
        "Couldn't save tutorial state. You may see this again next time.",
      )
    }
  }, [replayActive, updateUser, user?.tutorialCompletedAt])

  const replay = useCallback(() => {
    setReplayActive(true)
    // The server-side flag is already set; replays must never write again.
    persistedRef.current = true
  }, [])

  return { shouldRun, markComplete, replay }
}
